import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Room, RoomEvent, Track, RemoteTrack, RemoteTrackPublication, RemoteParticipant } from 'livekit-client';
import { unlockDoor } from '../../api/videoCall';
import { apiOrigin } from '../../utils/surface';
import { DeviceActionButton } from '../../components/access/DeviceVisuals';

const LIVEKIT_URL = 'wss://intrlock-j4f640sq.livekit.cloud';
const VIDEO_API = `${apiOrigin()}/video`;

type CallStatus = 'tap-to-answer' | 'connecting' | 'connected' | 'ended' | 'error';

export const TenantVideoCall: React.FC = () => {
  const { callId: roomId } = useParams();
  const [searchParams] = useSearchParams();
  // Token can come from query param or we fetch it via /video/token
  const tokenParam = searchParams.get('token') || '';
  const tenantId = searchParams.get('tenant_id') || '';
  const propertyId = searchParams.get('property_id') || '';

  const [status, setStatus] = useState<CallStatus>('tap-to-answer');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [doorUnlocking, setDoorUnlocking] = useState(false);
  const [doorMsg, setDoorMsg] = useState<string | null>(null);

  const roomRef = useRef<Room | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const durationRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (roomRef.current) roomRef.current.disconnect();
      if (durationRef.current) clearInterval(durationRef.current);
    };
  }, []);

  const connectToRoom = useCallback(async () => {
    if (!roomId) return;
    setStatus('connecting');
    setErrorMsg(null);

    try {
      let token = tokenParam;

      // If no token in URL, fetch one from the Lambda
      if (!token) {
        const identity = `tenant-${tenantId || Date.now()}`;
        const res = await fetch(`${VIDEO_API}/token?room=${encodeURIComponent(roomId)}&identity=${encodeURIComponent(identity)}`);
        if (!res.ok) throw new Error('Failed to get call token');
        const data = await res.json();
        token = data.token;
      }

      if (!token) throw new Error('No token available for this call');

      // Skip the gUM-then-stop dance: on iOS Safari that pattern leaves
      // the published mic muted intermittently. LiveKit re-acquires mic
      // and camera as part of setMicrophoneEnabled / setCameraEnabled
      // below, which prompts for permission cleanly.

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: { videoCodec: 'vp8' },
      });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, _participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Video && remoteVideoRef.current) {
          track.attach(remoteVideoRef.current);
        }
        if (track.kind === Track.Kind.Audio) {
          const audioEl = track.attach();
          document.body.appendChild(audioEl);
        }
        setStatus('connected');
        if (!durationRef.current) {
          durationRef.current = setInterval(() => setDuration(d => d + 1), 1000);
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        setStatus('ended');
        if (durationRef.current) { clearInterval(durationRef.current); durationRef.current = null; }
      });

      room.on(RoomEvent.ParticipantDisconnected, () => {
        room.disconnect();
      });

      await room.connect(LIVEKIT_URL, token);

      // Enable camera + mic individually so a partial failure (mic
      // unpublished) is loud in the console instead of a silent call.
      try {
        await room.localParticipant.setCameraEnabled(true);
      } catch (e: any) { console.warn('[TenantVideoCall] camera enable failed:', e?.message ?? e); }
      const ensureMic = async (attempt: number): Promise<void> => {
        await room.localParticipant.setMicrophoneEnabled(true);
        await new Promise((r) => setTimeout(r, 250));
        const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        if ((!pub || pub.isMuted || !pub.track) && attempt < 2) {
          await room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
          await new Promise((r) => setTimeout(r, 200));
          return ensureMic(attempt + 1);
        }
      };
      await ensureMic(0);

      // Attach local video
      const localTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (localTrack && localVideoRef.current) {
        localTrack.attach(localVideoRef.current);
      }

      // If no remote participant yet, show connecting — will update when TrackSubscribed fires
      if (room.remoteParticipants.size === 0) {
        setStatus('connecting');
      }

    } catch (err: any) {
      console.error('[TenantVideoCall] connect error:', err);
      setErrorMsg(err.message || 'Failed to connect');
      setStatus('error');
    }
  }, [roomId, tokenParam, tenantId]);

  const handleDisconnect = useCallback(() => {
    if (roomRef.current) roomRef.current.disconnect();
    setStatus('ended');
    if (durationRef.current) { clearInterval(durationRef.current); durationRef.current = null; }
  }, []);

  const toggleMute = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.localParticipant.setMicrophoneEnabled(muted);
      setMuted(m => !m);
    }
  }, [muted]);

  const toggleVideo = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.localParticipant.setCameraEnabled(!videoEnabled);
      setVideoEnabled(v => !v);
    }
  }, [videoEnabled]);

  const handleUnlock = async () => {
    setDoorUnlocking(true);
    setDoorMsg(null);
    try {
      const r = await unlockDoor(propertyId, tenantId, { roomName: roomId });
      setDoorMsg(r.message);
    } catch (e: any) {
      setDoorMsg(e.message || 'Unlock failed');
    } finally {
      setDoorUnlocking(false);
      setTimeout(() => setDoorMsg(null), 4000);
    }
  };

  const fmt = (s: number) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (!roomId) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#f87171', fontSize: 18 }}>Invalid call link — missing room ID</p>
      </div>
    );
  }

  // Tap to answer screen
  if (status === 'tap-to-answer') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>📹</div>
          <p style={{ color: '#fff', fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Incoming Video Call</p>
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 32 }}>A visitor is at your door</p>
          <button
            onClick={connectToRoom}
            style={{ background: '#16a34a', color: '#fff', fontSize: 18, fontWeight: 700, padding: '16px 48px', borderRadius: 16, border: 'none', cursor: 'pointer' }}
          >
            Answer Call
          </button>
        </div>
      </div>
    );
  }

  // Error screen
  if (status === 'error') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '0 24px' }}>
          <p style={{ color: '#f87171', fontSize: 18, marginBottom: 16 }}>{errorMsg || 'Something went wrong'}</p>
          <button
            onClick={connectToRoom}
            style={{ background: '#2563eb', color: '#fff', fontSize: 16, fontWeight: 600, padding: '12px 32px', borderRadius: 12, border: 'none', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main call UI
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', flexDirection: 'column' }}>
      {/* Remote video — full screen background */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'contain', background: '#000',
            display: status === 'connected' ? 'block' : 'none',
          }}
        />

        {/* Ended overlay */}
        {status === 'ended' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#fff', fontSize: 18 }}>Call ended</p>
              {duration > 0 && <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>{fmt(duration)}</p>}
              <p style={{ color: '#4b5563', fontSize: 14, marginTop: 16 }}>You may close this window</p>
            </div>
          </div>
        )}

        {/* Connecting overlay */}
        {status === 'connecting' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#fff', fontSize: 18 }}>Connecting...</p>
              <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 8 }}>Waiting for visitor's video</p>
            </div>
          </div>
        )}

        {/* Local PiP */}
        <div style={{ position: 'absolute', top: 60, right: 12, width: 96, height: 128, borderRadius: 8, overflow: 'hidden', border: '1px solid #4b5563', background: '#111' }}>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
          />
        </div>

        {/* Header */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '12px 16px', paddingTop: 'max(12px, env(safe-area-inset-top))', background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}><span style={{ color: '#60a5fa' }}>INTR</span>LOCK</div>
          {status === 'connected' && (
            <div style={{ background: 'rgba(0,0,0,0.6)', borderRadius: 20, padding: '4px 12px' }}>
              <span style={{ color: '#fff', fontSize: 12, fontFamily: 'monospace' }}>{fmt(duration)}</span>
            </div>
          )}
        </div>

        {/* Unlock toast */}
        {doorMsg && (
          <div style={{ position: 'absolute', top: 60, left: 16, right: 60, background: 'rgba(22,163,74,0.9)', color: '#fff', borderRadius: 12, padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 500, zIndex: 20 }}>
            {doorMsg}
          </div>
        )}
      </div>

      {/* Controls */}
      {status !== 'ended' && (
        <div style={{ background: '#0a0a0a', padding: '12px 24px', paddingBottom: 'max(2rem, env(safe-area-inset-bottom))', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 20 }}>
          <button
            onClick={toggleMute}
            style={{ width: 48, height: 48, borderRadius: 24, background: muted ? '#dc2626' : '#374151', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={muted ? 'M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2' : 'M15.536 8.464a5 5 0 010 7.072M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'} />
            </svg>
          </button>

          <DeviceActionButton
            surface="touch"
            onClick={handleUnlock}
            loading={doorUnlocking}
            loadingLabel="Unlocking…"
            icon={<svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>}
          >
            Unlock
          </DeviceActionButton>

          <button
            onClick={toggleVideo}
            style={{ width: 48, height: 48, borderRadius: 24, background: !videoEnabled ? '#dc2626' : '#374151', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>

          <button
            onClick={handleDisconnect}
            style={{ width: 48, height: 48, borderRadius: 24, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
